import { type JSX, useCallback, useEffect, useState } from 'react';

import { ApiFehler } from '../api/fehler';
import type { KiVorschlag } from '../api/ki';
import {
  FOLGEAKTIONSNAME,
  KATEGORIENAME,
  kiAuslesen,
  kiEntscheiden,
  kiPostfachstand,
  zuordnung as ladeZuordnung,
  type Bezugszeile,
  type Kategorie,
  type KiPostfachstand,
  type KiSchritt,
  type Mailauslesung,
  type Vorgangszeile,
} from '../api/support';
import { Kikennzeichnung } from '../bericht/KiBericht';
import { alsTag } from '../datum';
import { alsEuro } from '../betrag';
import { Meldung } from '../komponenten/Meldung';
import { markenklasse } from '../marken';
import { fange } from './bearbeitungshilfe';
import { folgeaktionMoeglich, folgeaktionOeffnen, type Folgewege } from './Folgeaktionen';

/**
 * DIE KI AM VORGANG (SUP-14, SUP-39, KI-01 bis KI-07; Auftrag CRM-179).
 *
 * Die Auslesung zeigt Anliegen, Kategorie, erkannte Daten, Stimmung und
 * Dringlichkeit, dazu nächste Schritte. Ein Schritt öffnet die Maske der
 * Folgeaktion — die KI führt nichts aus (KI-02). Ohne Kunden nennt sie einen
 * Kandidaten aus der Zuordnung (SUP-39); zugeordnet wird dort.
 *
 * Steht die KI aus — ab Werk, im Postfach oder für die Rolle — bleibt der
 * Vorgang ganz bedienbar (KI-07, SUP-34); hier steht dann nur, warum.
 */

const STIMMUNGSNAME: Readonly<Record<Mailauslesung['stimmung'], string>> = {
  positiv: 'positiv',
  neutral: 'neutral',
  unzufrieden: 'unzufrieden',
  veraergert: 'verärgert',
};

const DRINGLICHKEITSNAME: Readonly<Record<Mailauslesung['dringlichkeit'], string>> = {
  niedrig: 'niedrig',
  normal: 'normal',
  hoch: 'hoch',
};

/** Wohin eine Quelle führt — oder null, wenn es keine eigene Maske gibt. */
export type Quellenwege = Folgewege & { readonly kunde?: ((id: string) => void) | undefined };

/** KI-03, SUP-31: die Quellen eines Vorschlags; jede öffnet ihre Maske, soweit es eine gibt. */
export function KiQuellen({
  quellen,
  bezuege,
  wege,
}: {
  readonly quellen: KiVorschlag<unknown>['quellen'];
  readonly bezuege: readonly Bezugszeile[];
  readonly wege: Quellenwege;
}): JSX.Element {
  const oeffnen = (art: string, id: string): (() => void) | null => {
    if (art === 'kunde' && wege.kunde !== undefined) return () => wege.kunde?.(id);
    if (art === 'rechnung' && wege.rechnung !== undefined) return () => wege.rechnung?.(id);
    const buchung = art === 'buchung' ? bezuege.find((b) => b.art === 'buchung' && b.ziel === id) : undefined;
    if (buchung !== undefined && wege.buchung !== undefined) return () => wege.buchung?.(buchung.nummer, 'uebersicht');
    return null;
  };
  return (
    <ul className="kanalliste" aria-label="Quellen des Vorschlags">
      {quellen.map((q) => {
        const auf = oeffnen(q.art, q.id);
        return (
          <li key={`${q.art}-${q.id}`}>
            {q.bezeichnung}{' '}
            {auf !== null && (
              <button type="button" className="knopf knopf--klein" data-auch-telefon onClick={auf}>
                Öffnen
              </button>
            )}
          </li>
        );
      })}
    </ul>
  );
}

export function KiBereich({
  vorgang: v,
  ich,
  bearbeitbar,
  wege,
  beiKategorie,
  beiAntworten,
  beiWiedervorlage,
}: {
  readonly vorgang: Vorgangszeile;
  /** Der angemeldete Benutzer — über einen Vorschlag entscheidet nur, für wen er entstand. */
  readonly ich: string | undefined;
  /** support/bearbeiten und nicht auf dem Telefon (CRM-106). */
  readonly bearbeitbar: boolean;
  readonly wege: Quellenwege;
  /** SUP-14: die vorgeschlagene Kategorie setzen — mit dem Recht des Anwenders. */
  readonly beiKategorie: (k: Kategorie) => void;
  /** Ein Schritt „Rückfrage" öffnet die Antwort. */
  readonly beiAntworten: () => void;
  /** Ein Schritt „Wiedervorlage" öffnet ihren Dialog, vorbelegt. */
  readonly beiWiedervorlage: (vorbelegung: { readonly faelligAm: string | null; readonly beschreibung: string }) => void;
}): JSX.Element | null {
  const [stand, setzeStand] = useState<KiPostfachstand | null>(null);
  const [bezuege, setzeBezuege] = useState<readonly Bezugszeile[]>([]);
  const [kunde, setzeKunde] = useState<string | null>(v.kunde);
  const [fehler, setzeFehler] = useState<ApiFehler | null>(null);
  const [laeuft, setzeLaeuft] = useState(false);

  const laden = useCallback(async (): Promise<void> => {
    try {
      const [s, z] = await Promise.all([kiPostfachstand(v.id), ladeZuordnung(v.id)]);
      setzeStand(s);
      setzeBezuege(z.bezuege);
      setzeKunde(z.kunde?.id ?? null);
    } catch (f) {
      fange(setzeFehler, f);
    }
  }, [v.id]);

  useEffect(() => {
    void laden();
  }, [laden]);

  const tue = async (was: () => Promise<unknown>): Promise<void> => {
    setzeLaeuft(true);
    setzeFehler(null);
    try {
      await was();
      await laden();
    } catch (f) {
      fange(setzeFehler, f);
    } finally {
      setzeLaeuft(false);
    }
  };

  if (stand === null) return fehler !== null ? <Meldung fehler={fehler} /> : null;
  // Eine verworfene Auslesung zeigt niemand mehr; neu auslesen geht.
  const a = stand.letzteAuslesung?.entscheidung === 'verworfen' ? null : stand.letzteAuslesung;
  const eigene = a !== null && a.erzeugtFuer === ich;
  // KI-07: Ohne KI und ohne frühere Auslesung bleibt es bei einer Zeile.
  if (a === null && !stand.auslesung.verfuegbar) {
    return bearbeitbar ? <p className="hilfe">KI-Auslesung nicht verfügbar: {stand.auslesung.hinweis}</p> : null;
  }

  const schritt = (s: KiSchritt): { readonly grund: string | null; readonly los: () => void } => {
    if (s.aktion === 'rueckfrage') return { grund: null, los: beiAntworten };
    if (s.aktion === 'wiedervorlage') {
      return { grund: null, los: () => beiWiedervorlage({ faelligAm: s.vorbelegung.termin, beschreibung: s.text }) };
    }
    const art = s.aktion;
    // Das Ziel aus der Auslesung, sonst der erste Bezug — wie in der Auswahl der Folgeaktionen.
    const passend = (z: 'buchung' | 'rechnung'): Bezugszeile | undefined =>
      bezuege.find((b) => b.art === z && s.ziel?.art === z && b.nummer === s.ziel.nummer) ?? bezuege.find((b) => b.art === z);
    const ziel = { kunde, buchung: passend('buchung'), rechnung: passend('rechnung') };
    return {
      grund: folgeaktionMoeglich(art, wege, kunde, ziel.buchung, ziel.rechnung),
      los: () => void tue(() => folgeaktionOeffnen(v, art, ziel, wege)),
    };
  };

  return (
    <section className="vorgang__ki" aria-label="KI-Auslesung">
      <h3>KI-Auslesung</h3>
      {fehler !== null && <Meldung fehler={fehler} />}
      {a !== null && (
        <>
          <Kikennzeichnung kennzeichnung={a.kennzeichnung} />
          <p>
            <strong>{a.entwurf.anliegen}</strong>
          </p>
          <dl className="vorgang__kidaten">
            <dt>Kategorie</dt>
            <dd>
              {KATEGORIENAME[a.entwurf.kategorie]}{' '}
              {bearbeitbar && (
                <button type="button" className="knopf knopf--klein" disabled={laeuft} onClick={() => beiKategorie(a.entwurf.kategorie)}>
                  Übernehmen
                </button>
              )}
            </dd>
            <dt>Stimmung</dt>
            <dd>
              <span className={markenklasse(a.entwurf.stimmung === 'veraergert' || a.entwurf.stimmung === 'unzufrieden' ? 'aufmerksamkeit' : 'laufend')}>
                {STIMMUNGSNAME[a.entwurf.stimmung]}
              </span>
            </dd>
            <dt>Dringlichkeit</dt>
            <dd>
              <span className={markenklasse(a.entwurf.dringlichkeit === 'hoch' ? 'aufmerksamkeit' : 'laufend')}>
                {DRINGLICHKEITSNAME[a.entwurf.dringlichkeit]}
              </span>{' '}
              <span className="hilfe">{a.entwurf.dringlichkeitGrund}</span>
            </dd>
            {a.entwurf.daten.termine.length > 0 && (
              <>
                <dt>Termine</dt>
                <dd>
                  {a.entwurf.daten.termine
                    .map((t) => `${alsTag(t.datum) ?? t.datum}${t.uhrzeit === null ? '' : `, ${t.uhrzeit} Uhr`} (${t.bedeutung})`)
                    .join('; ')}
                </dd>
              </>
            )}
            {a.entwurf.daten.fristen.length > 0 && (
              <>
                <dt>Fristen</dt>
                <dd>{a.entwurf.daten.fristen.map((f) => `${alsTag(f.datum) ?? f.datum} (${f.bedeutung})`).join('; ')}</dd>
              </>
            )}
            {a.entwurf.daten.personen.length > 0 && (
              <>
                <dt>Personen</dt>
                <dd>{a.entwurf.daten.personen.map((p) => `${p.name} (${p.rolle})`).join('; ')}</dd>
              </>
            )}
            {a.entwurf.daten.nummern.length > 0 && (
              <>
                <dt>Nummern</dt>
                <dd>{a.entwurf.daten.nummern.map((n) => n.nummer).join(', ')}</dd>
              </>
            )}
            {a.entwurf.daten.betraege.length > 0 && (
              <>
                <dt>Beträge</dt>
                <dd>{a.entwurf.daten.betraege.map((b) => `${alsEuro(b.betrag)} (${b.bedeutung})`).join('; ')}</dd>
              </>
            )}
          </dl>
          {/* SUP-39: zugeordnet wird in der Zuordnung, mit dem Recht des Anwenders. */}
          {a.entwurf.zuordnung !== null && (
            <p className="hinweiszeile">
              {a.entwurf.zuordnung.kunde === null
                ? `Keinen passenden Kunden erkannt (${a.entwurf.zuordnung.grund}).`
                : `Vorschlag zur Zuordnung: ${a.entwurf.zuordnung.kunde.name} (${a.entwurf.zuordnung.kunde.kundennummer}) — ${a.entwurf.zuordnung.grund}. Zuordnen unter „Kunde".`}
            </p>
          )}
          {a.entwurf.schritte.length > 0 && (
            <ul className="zuordnung__bezuege" aria-label="Nächste Schritte">
              {a.entwurf.schritte.map((s, i) => {
                const { grund, los } = schritt(s);
                const vorbelegt = [
                  s.ziel?.nummer,
                  s.vorbelegung.teilnehmer === null ? undefined : `${String(s.vorbelegung.teilnehmer)} Teilnehmende`,
                  s.vorbelegung.termin === null ? undefined : alsTag(s.vorbelegung.termin) ?? s.vorbelegung.termin,
                ].filter((x) => x !== undefined);
                return (
                  <li key={`${s.aktion}-${String(i)}`}>
                    {bearbeitbar ? (
                      <button
                        type="button"
                        className={s.aktion === 'buchung_stornieren' ? 'knopf knopf--klein knopf--eingriff' : 'knopf knopf--klein'}
                        disabled={laeuft || grund !== null}
                        onClick={los}
                      >
                        {s.aktion === 'rueckfrage' ? 'Rückfrage' : FOLGEAKTIONSNAME[s.aktion]}
                      </button>
                    ) : (
                      <strong>{s.aktion === 'rueckfrage' ? 'Rückfrage' : FOLGEAKTIONSNAME[s.aktion]}</strong>
                    )}{' '}
                    {s.text}
                    {vorbelegt.length > 0 && <span className="hilfe"> · {vorbelegt.join(' · ')}</span>}
                    {grund !== null && (
                      <>
                        <br />
                        <span className="hilfe">{grund}</span>
                      </>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
          <details>
            <summary>Quellen</summary>
            <KiQuellen quellen={a.quellen} bezuege={bezuege} wege={wege} />
          </details>
        </>
      )}
      {bearbeitbar && (
        <div className="knopfreihe">
          {/* KI-04, KI-08: ob die Auslesung geholfen hat; der Abbruch steht links (CRM-87). */}
          {eigene && a.entscheidung === null && (
            <button type="button" className="knopf knopf--klein knopf--eingriff" disabled={laeuft} onClick={() => void tue(() => kiEntscheiden(a.id, 'verwerfen'))}>
              Verwerfen
            </button>
          )}
          {stand.auslesung.verfuegbar ? (
            <button type="button" className="knopf knopf--klein" disabled={laeuft} onClick={() => void tue(() => kiAuslesen(v.id))}>
              {a === null ? 'Mit KI auslesen' : 'Neu auslesen'}
            </button>
          ) : (
            <span className="hilfe">{stand.auslesung.hinweis}</span>
          )}
          {eigene && a.entscheidung === null && (
            <button type="button" className="knopf knopf--klein" disabled={laeuft} onClick={() => void tue(() => kiEntscheiden(a.id, 'uebernehmen'))}>
              Hilfreich
            </button>
          )}
          {a !== null && a.entscheidung !== null && <span className="hilfe">Als hilfreich vermerkt.</span>}
        </div>
      )}
    </section>
  );
}
